/**
 * Turning a recording into the shape drawn behind the voice player's scrubber.
 *
 * Signal processing rather than rendering, so it sits beside the player instead
 * of inside it - it is the part with no React in it and the part worth reading
 * on its own.
 */

/**
 * The shape is analysed at a fixed resolution and resampled onto the bars that
 * are actually drawn, so the drawing is independent of the decode.
 */
export const PEAK_RESOLUTION = 320;

/** Flat bars while the file is still downloading, or if it cannot be decoded. */
export const FLAT_PEAKS = Array.from({ length: PEAK_RESOLUTION }, () => 0.35);

/** Squeezes the analysed shape onto the bars there is actually room for. */
export const resamplePeaks = (peaks: number[], count: number) =>
  Array.from({ length: count }, (_, index) => {
    const start = Math.floor((index * peaks.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((index + 1) * peaks.length) / count)
    );
    let max = 0;
    for (let i = start; i < end && i < peaks.length; i += 1) {
      if (peaks[i] > max) max = peaks[i];
    }
    return max;
  });

/**
 * One bar per bucket, as RMS rather than the loudest sample: peak amplitude is
 * dominated by clicks and breaths, which is what made the first version read as
 * a flat line with one spike in it.
 *
 * Normalised against a high percentile, not the maximum, for the same reason,
 * then curved so ordinary speech uses the top half of the bar rather than
 * hugging the floor. Sampled rather than scanned - a two-minute recording is
 * some five million frames, and its shape does not change for reading them all.
 */
export const buildPeaks = (buffer: AudioBuffer) => {
  const data = buffer.getChannelData(0);
  const block = Math.floor(data.length / PEAK_RESOLUTION) || 1;
  const peaks: number[] = [];

  for (let index = 0; index < PEAK_RESOLUTION; index += 1) {
    const start = index * block;
    const end = Math.min(start + block, data.length);
    const step = Math.max(1, Math.floor((end - start) / 400));
    let sum = 0;
    let count = 0;
    for (let frame = start; frame < end; frame += step) {
      sum += data[frame] * data[frame];
      count += 1;
    }
    peaks.push(count ? Math.sqrt(sum / count) : 0);
  }

  const sorted = [...peaks].sort((a, b) => a - b);
  const reference = sorted[Math.floor(sorted.length * 0.9)];
  if (!reference) return FLAT_PEAKS;

  return peaks.map((peak) => Math.min(1, (peak / reference) ** 0.7));
};

/**
 * Fetches a voice note and hands back both a playable object URL and its shape.
 *
 * The file is fetched as a blob for two reasons: Spaces does not answer range
 * requests, so a plain `src` is unseekable, and the same bytes are what the
 * peaks are computed from - the waveform costs no extra request.
 *
 * `onPlayable` fires the moment the blob exists, before the decode: the note is
 * playable as soon as it has downloaded, and waiting for the waveform to be
 * analysed would leave the play button disabled for no reason.
 */
export const loadVoiceNote = async (
  url: string,
  mime: string | null,
  onPlayable: (objectUrl: string) => void
) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(String(response.status));
  const buffer = await response.arrayBuffer();

  /*
   * The type is not optional here, however much it looks it.
   *
   * A typeless Blob gets `text/plain`, and while Chrome and Firefox sniff the
   * bytes and play it anyway, WebKit trusts the declared type and refuses to
   * decode a blob: URL that claims to be text - so every voice note was mute on
   * every iPhone, in Safari and in Chrome (which is Safari), while working
   * perfectly on the desktop it was tested on.
   *
   * The attachment's own mime first, the response's second: Spaces serves what
   * it was given at upload, which is the same value, but a note uploaded before
   * that was recorded correctly may have neither.
   */
  const type = mime || response.headers.get('content-type') || 'audio/mp4';
  onPlayable(URL.createObjectURL(new Blob([buffer], { type })));

  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return { peaks: FLAT_PEAKS, duration: 0 };

  const context = new Ctor();
  try {
    // decodeAudioData detaches the buffer it is given, hence the copy.
    const decoded = await context.decodeAudioData(buffer.slice(0));
    return { peaks: buildPeaks(decoded), duration: decoded.duration || 0 };
  } catch {
    // A container this browser cannot decode still plays through the element
    // often enough; keep the flat bars and carry on.
    return { peaks: FLAT_PEAKS, duration: 0 };
  } finally {
    context.close().catch(() => undefined);
  }
};
